import uuid from 'uuid-v4';
import get from 'lodash/get';
import ngCookies from 'angular-cookies';
import 'constants/constants.json';
import { SAVED_CART_KEY } from 'app/constants';
import { FacebookPixelService } from './facebookPixel.service';
import { GaService } from './ga.service';
import { ApiService } from './api.service';
import { CreditCardService, createStripeTokenMetadata } from './creditCard.service';

export const OrderService = angular
    .module('checkout.orderService', [
        ngCookies,
        FacebookPixelService,
        GaService,
        ApiService,
        CreditCardService,
        'app.constants'
    ]).service('orderService', (
        $q,
        $window,
        $cookies,
        apiService,
        creditCardService,
        gaService,
        facebookPixelService,
        CART_LOCAL_STORAGE_KEY,
        TENANT_LOCAL_STORAGE_KEY
    ) => {
        function placeOrder(orderOptions) {
            const {
                cart,
                authorizeNet,
                paymentMethod,
                card,
                stripe,
                stripeElementsCard,
                stripeCreditCardId
            } = orderOptions;
            const { publicClientKey, apiLoginId } = authorizeNet;

            if (paymentMethod === 'Credit Card' && authorizeNet.isEnabled && card) {
                return creditCardService.createAuthorizeNetToken({ card, cart, publicClientKey, apiLoginId })
                    .then(response => submitOrderPayload({ ...orderOptions, authorizeNetToken: response.opaqueData }))
                    .then((order) => {
                        handleOrderCreated(order);
                        return order;
                    });
            } else if (paymentMethod === 'Credit Card' && stripe && stripeElementsCard && !stripeCreditCardId) {
                const deferred = $q.defer();
                orderOptions.stripe.createToken(
                    orderOptions.stripeElementsCard,
                    createStripeTokenMetadata(cart.billingAddress)
                )
                    .then(({ token, error }) => {
                        if (error) throw new Error(error.message);
                        return submitOrderPayload({ ...orderOptions, stripeToken: token });
                    })
                    .then((order) => {
                        handleOrderCreated(order);
                        return order;
                    })
                    .then(deferred.resolve)
                    .catch(deferred.reject);
                return deferred.promise;
            }

            return submitOrderPayload(orderOptions)
                .then((order) => {
                    handleOrderCreated(order);
                    return order;
                });
        }

        function submitOrderPayload(orderOptions) {
            const {
                cart,
                grandTotal,
                shopperNote,
                card,
                creditCardType,
                payPalExpressCheckoutToken,
                payPalPayerId,
                paymentMethod,
                customerId,
                storeCreditInfo,
                stripeCreditCardId,
                stripeToken,
                authorizeNetToken,
                taxProvider
            } = orderOptions;
            const creditCardLastFour = getCreditCardLastFour(card);

            const orderPayload = getOrderPayload({ cart, shopperNote });

            if (customerId) {
                orderPayload.customerId = customerId;
            }

            if (stripeCreditCardId) {
                orderPayload.stripeCreditCardId = stripeCreditCardId;
            }

            if (creditCardLastFour) {
                orderPayload.creditCardLastFour = creditCardLastFour;
            }
            if (creditCardType) {
                orderPayload.creditCardType = creditCardType;
            }

            if (taxProvider) {
                orderPayload.taxProvider = taxProvider;
            }

            const paymentRequests = [];
            storeCreditInfo.items.forEach((item) => {
                paymentRequests.push({
                    amount: item.amountUsedInCents / 100,
                    type: 'StoreCredit',
                    paymentDetails: {
                        giftCardCode: item.giftCardCode
                    }
                });
            });

            const chargeTotal = getChargeTotal({ cart, grandTotal, storeCreditInfo });
            if (chargeTotal > 0 || paymentRequests.length === 0) {
                if (stripeToken || stripeCreditCardId) {
                    const paymentDetails = {
                        customerId,
                        stripeCreditCardId
                    };
                    if (stripeToken) {
                        paymentDetails.stripeCreditCardToken = stripeToken.id;
                    }
                    paymentRequests.push({
                        amount: chargeTotal,
                        type: 'Stripe',
                        paymentDetails
                    });
                } else if (authorizeNetToken) {
                    paymentRequests.push({
                        amount: chargeTotal,
                        type: 'Authorize.net',
                        paymentDetails: {
                            authorizeNetOpaqueDataDescriptor: authorizeNetToken.dataDescriptor,
                            authorizeNetOpaqueDataValue: authorizeNetToken.dataValue,
                            creditCardLastFour,
                            creditCardType
                        }
                    });
                } else if (payPalExpressCheckoutToken) {
                    paymentRequests.push({
                        amount: chargeTotal,
                        type: 'PayPal',
                        paymentDetails: {
                            token: payPalExpressCheckoutToken,
                            creditCardLastFour,
                            creditCardType,
                            payPalPayerId
                        }
                    });
                } else if (paymentMethod === 'Cash') {
                    paymentRequests.push({
                        amount: chargeTotal,
                        type: 'Cash',
                        paymentDetails: {
                            type: 'Cash'
                        }
                    });
                }
            }

            orderPayload.paymentRequests = paymentRequests;

            return apiService.createOrder(orderPayload)
                .catch((err) => {
                    throw getError('Error creating the order', err);
                });
        }

        function getCreditCardLastFour(card) {
            if (!card) {
                return null;
            }

            if (card.cardNumber) {
                return card.cardNumber.slice(-4);
            }
            return card.creditCardLastFour || null;
        }

        function getOrderPayload({ cart, shopperNote }) {
            const REQUEST_ID_CHAR_LIMIT = 20;
            const requestId = uuid().replace(/-/g, '').substring(0, REQUEST_ID_CHAR_LIMIT);

            // TODO: remove when legacy carts are deprecated
            if (!cart.cartId) {
                return {
                    taxAmount: cart.taxAmount,
                    taxRate: cart.taxRate,
                    grandTotal: cart.grandTotal,
                    placedBy: {
                        firstName: cart.billingAddress.firstName,
                        lastName: cart.billingAddress.lastName,
                        emailAddress: cart.contactInfo.email,
                        phoneNumber: cart.contactInfo.phoneNumber,
                        emailOptIn: cart.emailOptIn,
                        address: cart.billingAddress
                    },
                    deliverTo: {
                        firstName: cart.shippingAddress.firstName,
                        lastName: cart.shippingAddress.lastName,
                        address: cart.shippingAddress,
                        isResidential: true
                    },
                    cart,
                    shopperNote,
                    requestId
                };
            }

            // @FIXME: this transform is needed for "new" carts
            //         because the API expects the shipping method in the shape found in the legacy cart
            const transformedShippingMethod = {
                name: cart.shippingMethod.name,
                shippingBand: {
                    shippingPrice: cart.shippingMethod.shippingPrice
                }
            };

            return {
                placedBy: {
                    firstName: cart.billingAddress.firstName,
                    lastName: cart.billingAddress.lastName,
                    emailAddress: cart.contactInfo.email,
                    phoneNumber: cart.contactInfo.phoneNumber,
                    emailOptIn: cart.emailOptIn,
                    address: cart.billingAddress
                },
                deliverTo: {
                    firstName: cart.shippingAddress.firstName,
                    lastName: cart.shippingAddress.lastName,
                    address: cart.shippingAddress,
                    isResidential: true
                },
                cartId: cart.cartId,
                shippingMethod: transformedShippingMethod,
                shopperNote,
                requestId
            };
        }

        function getChargeTotal({ cart, grandTotal, storeCreditInfo }) {
            // TODO: remove when legacy carts are deprecated, possibly this entire function
            if (!cart.cartId) {
                return cart.grandTotal - (storeCreditInfo.storeCreditAmountInCents / 100);
            }

            return grandTotal;
        }

        function handleOrderCreated(order) {
            gaService.logPurchase(order);
            facebookPixelService.trackPurchaseEvent(order.cart);
            clearCartSession();
        }

        function clearCartSession() {
            $window.sessionStorage.removeItem(CART_LOCAL_STORAGE_KEY);
            $window.sessionStorage.removeItem(TENANT_LOCAL_STORAGE_KEY);
            $window.sessionStorage.removeItem(SAVED_CART_KEY);
            $cookies.remove(CART_LOCAL_STORAGE_KEY);
            $cookies.remove(TENANT_LOCAL_STORAGE_KEY);
        }

        function getError(preface, err) {
            const errorDetail = getErrorDetail(err);
            return new Error(`${preface}: ${errorDetail}`);
        }

        function friendlyModelError(error) {
            if (error['order.PlacedBy.PhoneNumber']) return 'Invalid Phone Number';
            return undefined;
        }

        function friendlyMessageError(error) {
            if (error === 'Error creating the order: Missing or invalid parameter: Address Invalid Address Provided') {
                return `We couldn't verify your address. You can try fixing it by checking for misspellings,
                    or by moving your suite/apartment/unit number/company name to Address line 2`;
            }
            // eslint-disable-next-line max-len
            if (error === 'Error creating the order: Missing or invalid parameter: order paymentMethod value must be one of Cash,Credit Card,PayPal,Purchase Order') {
                return `There was a problem creating the order. Please check your payment
                    information and try again.`;
            }
            return error;
        }

        function getErrorDetail(response) {
            const dataItemsError = get(response, 'data.items[0].message');
            const itemsError = get(response, 'items[0].message');
            const modelError = friendlyModelError(get(response, 'data.modelState', {}));
            const messageError = friendlyMessageError(get(response, 'message'));
            const stringError = typeof response === 'string' ? response : undefined;
            const unknownDataError = JSON.stringify(get(response, 'data'));
            const unknownError = JSON.stringify(response);
            return dataItemsError || itemsError || modelError || messageError || stringError ||
                unknownDataError || unknownError;
        }

        const service = {
            getErrorDetail,
            handleOrderCreated,
            placeOrder,
            submitOrderPayload
        };

        return service;
    }).name;



// WEBPACK FOOTER //
// ./src/app/services/order.service.js